import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type SelectHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

/**
 * The select primitive (VISUAL_SYSTEM_PLAN U3.1).
 *
 * Five `<select>` elements in five files, and five different class strings —
 * two heights (`h-9`, `h-10`), two names for the same border value
 * (`border-input`, `border-border-subtle`), a focus ring on two of the five,
 * `disabled:opacity-50` on three, and two of the files carrying a comment that
 * says in as many words "styled to match `Input`, since there is no ui/select
 * primitive". This is that primitive; those comments are now false and are
 * removed with the migration.
 *
 * **It wraps the NATIVE `<select>`, not `@radix-ui/react-select`.** The Radix
 * dependency is installed and this is a deliberate choice against it for now,
 * not an oversight:
 *
 * - All five call sites are `<select>` + `<option>` today. Radix's Select is a
 *   different component with a different API — a portalled listbox, `<Item>`
 *   children, `onValueChange` instead of `onChange` — so adopting it would be
 *   a rewrite of five controls and the five test files that drive them with
 *   `fireEvent.change`. That is a control redesign; this unit's scope is the
 *   missing primitive.
 * - Native keeps the platform picker on touch devices, `<option>` semantics,
 *   and form behaviour, none of which anyone has asked to change.
 *
 * When a call site needs what native cannot do — option groups with icons, a
 * searchable list, styling the open list itself — that is the moment for
 * Radix, and the swap has exactly one place to happen once every site imports
 * from here. Until then a portalled listbox is a second way to express the
 * same control, which is what this unit exists to stop.
 *
 * The chevron is the browser's. `appearance-none` plus a background-image
 * arrow is the usual next step and it is not taken here: it trades a
 * platform-correct affordance for a hand-drawn one, and the plan's C3 bar for
 * non-text contrast applies to that arrow the moment we own it.
 */

const selectVariants = cva(
  "rounded-md border border-input bg-background text-sm text-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        /** Matches `Input` — use it wherever a select sits beside one. */
        default: "h-10 px-3 py-2",
        /** The filter-toolbar height, beside `Button size="sm"`. */
        sm: "h-9 px-2",
      },
    },
    defaultVariants: { size: "default" },
  }
);

/**
 * `size` is the cva variant, so the native `size` attribute (the number of
 * visible rows, which turns a `<select>` into a list box) is omitted rather
 * than silently shadowed with a string. Nothing here uses it; a caller that
 * ever needs a multi-row list box should get its own primitive rather than a
 * prop that means two different things.
 */
export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size">,
    VariantProps<typeof selectVariants> {}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, size, ...props }, ref) => (
    <select ref={ref} className={cn(selectVariants({ size }), className)} {...props} />
  )
);
Select.displayName = "Select";
